// 🔥 UPDATED: Integrated with BackgroundUploader for real-time progress tracking

import { Eye } from 'lucide-react';
import { UploadCalendar } from '@/components/ui/upload-calendar'
import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UploadDateTimePicker } from '@/components/ui/upload-date-time-picker'
import { MediaPreviewCard } from '@/components/upload/media-preview-card';
import { useAuth } from '@/features/auth/auth-context';
import { uploadToCloudinary } from '@/lib/uploadToCloudinary';
import { useVideoConversion } from '@/hooks/useVideoConversion';
import { checkBrowserSupport } from '@/lib/convertToVideo';
import { AlertCircle, CheckCircle2, ImageIcon, Zap, Clock, Gauge, Upload, CloudUpload, Youtube, RefreshCw } from 'lucide-react';
import { parseISO, isValid, format } from 'date-fns';
import { supabase } from '@/lib/supabaseClient';
import { upsertSupabaseDraft } from '@/lib/supabaseDrafts';
import type { Session } from '@supabase/supabase-js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUploadQueue } from '@/components/upload-queue-context';
import { deleteUploadRetry, saveUploadRetry, updateUploadRetry } from '@/lib/uploadRetryStore';


// 🆕 Global storage for queued upload data (Files can't be serialized to sessionStorage)
const queuedUploadsData = new Map<string, {
  data: any;
  userId: string;
  session: Session;
}>();

// Expose on window for queue processor
if (typeof window !== 'undefined') {
  window.queuedUploadsData = queuedUploadsData;
}

interface StepScheduleProps {
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  onPrevious: () => void;
  onSaveDraft: () => void;
  onSubmit: (videoId: string, youtubeUrl: string, thumbnailUrl?: string) => void;
}

interface UploadProgress {
  step: string;
  message: string;
  timestamp: string;
  requestId?: string;
}

interface UploadResult {
  success: boolean;
  videoId: string;
  youtubeUrl: string;
  requestId: string;
  timing?: Record<string, number>;
}

interface UploadError {
  error: true;
  step: string;
  message: string;
  code: string;
  requestId: string;
}

interface UploadItem {
  id: string;
  title: string;
  platform: "youtube";
  status: "scheduled" | "published";
}

export function StepSchedule({ data, setData, onPrevious, onSaveDraft, onSubmit }: StepScheduleProps) {
  const { user } = useAuth();
  const { addUpload, updateUpload, uploads } = useUploadQueue();
  
  const [audioSrc, setAudioSrc] = useState('');
  const [videoSrc, setVideoSrc] = useState('');
  const [imageSrc, setImageSrc] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [uploadStage, setUploadStage] = useState('');
  const [itemsByDate, setItemsByDate] = useState<Record<string, UploadItem[]>>({});
  const [highlightedDates, setHighlightedDates] = useState({ 
    scheduled: [], 
    published: [] 
  });

  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [uploadRequestId, setUploadRequestId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<UploadError | null>(null);
  const [shouldUseJsonFallback, setShouldUseJsonFallback] = useState(false);
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [serverStatus, setServerStatus] = useState<'checking' | 'online' | 'offline' | null>(null);
  const [currentUploadId, setCurrentUploadId] = useState<string | null>(null);

  const showUploadError = (message: string, code = 'UPLOAD_ERROR') => {
    setUploadError({
      error: true,
      step: 'upload',
      message,
      code,
      requestId: currentUploadId || data.id || 'upload',
    });
  };

  const {
    isConverting,
    progress: conversionProgress,
    error: conversionError,
    estimatedTime,
    conversionSpeed,
    convert: convertVideo,
    reset: resetConversion
  } = useVideoConversion();

  // 🆕 Sync conversion progress to BackgroundUploader
  useEffect(() => {
    if (isConverting && currentUploadId) {
      if (conversionProgress === 0) {
        // Just started converting
        updateUpload(currentUploadId, { 
          progress: 5,
          timeLeft: 'Converting audio...'
        });
      } else if (conversionProgress > 0) {
        // Map conversion progress (0-100) to upload progress (5-30)
        const mappedProgress = 5 + (conversionProgress * 0.25);
        updateUpload(currentUploadId, { 
          progress: Math.round(mappedProgress),
          timeLeft: estimatedTime || `Converting... ${conversionProgress}%`
        });
      }
    }
  }, [conversionProgress, isConverting, currentUploadId, estimatedTime, updateUpload]);

  useEffect(() => {
    const checkServerStatus = async () => {
      if (!import.meta.env.VITE_API_BASE_URL) {
        console.warn('⚠️ VITE_API_BASE_URL not configured');
        return;
      }

      setServerStatus('checking');
      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/health`, {
          method: 'GET',
          signal: AbortSignal.timeout(5000)
        });
        setServerStatus(response.ok ? 'online' : 'offline');
      } catch (err) {
        console.warn('Server connectivity check failed:', err);
        setServerStatus('offline');
      }
    };

    checkServerStatus();
  }, []);

  const validateAndGetSession = async (): Promise<Session | null> => {
    try {
      console.log('🔍 Validating session...');
      
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('❌ Session validation error:', error);
        setSessionError(`Session error: ${error.message}`);
        return null;
      }
      
      if (!session) {
        console.error('❌ No active session found');
        setSessionError('No active session. Please sign in.');
        return null;
      }
      
      if (!session.access_token || !session.user?.id) {
        console.error('❌ Invalid session structure');
        setSessionError('Invalid session. Please sign in again.');
        return null;
      }
      
      if (session.expires_at) {
        const expiresAt = new Date(session.expires_at * 1000);
        const now = new Date();
        const timeUntilExpiry = expiresAt.getTime() - now.getTime();
        
        if (timeUntilExpiry < 5 * 60 * 1000) {
          console.warn('⚠️ Session expires soon, attempting refresh...');
          
          const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError || !refreshedSession) {
            console.error('❌ Session refresh failed:', refreshError);
            setSessionError('Session expired. Please sign in again.');
            return null;
          }
          
          setCurrentSession(refreshedSession);
          setSessionError(null);
          console.log('✅ Session refreshed successfully');
          return refreshedSession;
        }
      }
      
      setCurrentSession(session);
      setSessionError(null);
      console.log('✅ Session validated successfully');
      
      return session;
    } catch (err: any) {
      console.error('❌ Session validation failed:', err);
      setSessionError(err.message || 'Session validation failed');
      setCurrentSession(null);
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;
    
    const initializeSession = async () => {
      try {
        console.log('🔄 Initializing session...');
        const session = await validateAndGetSession();
        
        if (!mounted) return;
        
        setIsSessionReady(true);
        
        if (session) {
          console.log('✅ Session initialized successfully');
        } else {
          console.warn('⚠️ No valid session found during initialization');
        }
      } catch (err: any) {
        if (!mounted) return;
        
        console.error('❌ Session initialization failed:', err);
        setSessionError(`Failed to initialize session: ${err.message}`);
        setIsSessionReady(true);
      }
    };

    initializeSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      
      console.log('🔄 Auth state changed:', event);
      
      setCurrentSession(session);
      setIsSessionReady(true);
      
      if (event === 'SIGNED_OUT') {
        setSessionError('Session expired. Please sign in again.');
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('✅ Token refreshed successfully');
        setSessionError(null);
      } else if (event === 'SIGNED_IN') {
        console.log('✅ User signed in');
        setSessionError(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const resolveSrc = (input: File | string | null) => {
      if (!input) return '';
      if (typeof input === 'string') return input;
      return URL.createObjectURL(input);
    };

    setAudioSrc(resolveSrc(data.audio));
    setVideoSrc(resolveSrc(data.video));
    setImageSrc(resolveSrc(data.customThumbnail));

    return () => {
      if (data.audio instanceof File && audioSrc) URL.revokeObjectURL(audioSrc);
      if (data.video instanceof File && videoSrc) URL.revokeObjectURL(videoSrc);
      if (data.customThumbnail instanceof File && imageSrc) URL.revokeObjectURL(imageSrc);
    };
  }, [data.audio, data.video, data.customThumbnail]);

  useEffect(() => {
    const fetchDates = async () => {
      if (!user || !isSessionReady) return;
      
      try {
        const session = await validateAndGetSession();
        if (!session) return;
        
        const { data: uploads, error } = await supabase
          .from('uploads')
          .select('id, scheduled_time, status, published_at, created_at, title, youtube_video_id')
          .eq('user_id', user.id);

        if (error) {
          console.error('Failed to fetch upload dates:', error);
          return;
        }

        const scheduled = uploads
          .filter(item => item.status === 'scheduled' && item.scheduled_time)
          .map((item) => parseISO(item.scheduled_time))
          .filter((d) => d instanceof Date && isValid(d));

        const published = uploads
          .filter(item => item.status === 'published')
          .map((item) => {
            const dateStr = item.scheduled_time || item.published_at || item.created_at;
            return dateStr ? parseISO(dateStr) : null;
          })
          .filter((d) => d && isValid(d));

        const itemsByDateMap: Record<string, UploadItem[]> = {};

        uploads.forEach((upload) => {
          if (!upload.title) return;
          
          const relevantDate = upload.status === 'published' 
            ? upload.published_at 
            : upload.scheduled_time;
          
          if (relevantDate) {
            try {
              const dateKey = format(parseISO(relevantDate), 'yyyy-MM-dd');
              
              if (!itemsByDateMap[dateKey]) {
                itemsByDateMap[dateKey] = [];
              }
              
              itemsByDateMap[dateKey].push({
                id: upload.youtube_video_id || upload.id || `upload-${Date.now()}`,
                title: upload.title,
                platform: "youtube" as const,
                status: upload.status as "scheduled" | "published"
              });
            } catch (dateError) {
              console.warn('Failed to parse date for upload:', upload.id, relevantDate);
            }
          }
        });

        setHighlightedDates({ scheduled, published });
        setItemsByDate(itemsByDateMap);
        
      } catch (error) {
        console.warn('Failed to fetch scheduled dates:', error);
      }
    };

    fetchDates();
  }, [user, isSessionReady]);

  const validateFiles = () => {
    const hasVideo = data.video instanceof File;
    const hasAudio = data.audio instanceof File;
    const hasThumb = typeof data.customThumbnail === 'string' || data.customThumbnail instanceof File;

    if (!hasVideo && !(hasAudio && hasThumb)) {
      return { isValid: false, message: 'Please provide either a video or an audio + thumbnail.' };
    }
    if (hasAudio && !hasThumb) {
      return { isValid: false, message: 'Audio uploads require a thumbnail. Please go back to Step 1 and add one.' };
    }
    if (hasVideo && data.video.size > 100 * 1024 * 1024) {
      return { isValid: false, message: 'Video must be under 100MB.' };
    }
    if (hasAudio && data.audio.size > 50 * 1024 * 1024) {
      return { isValid: false, message: 'Audio must be under 50MB.' };
    }

    if (hasAudio && !hasVideo) {
      const browserSupport = checkBrowserSupport();
      if (!browserSupport.supported) {
        return { 
          isValid: false, 
          message: `Your browser doesn't support audio conversion: ${browserSupport.issues.join(', ')}. Please upload a video file instead or try a different browser.` 
        };
      }
    }

    return { isValid: true };
  };

  const handleThumbnailUpload = async (file: File) => {
    if (!file?.type.startsWith('image/')) {
      showUploadError('Only image files are allowed.', 'INVALID_THUMBNAIL');
      return;
    }
    
    try {
      setIsUploading(true);
      const session = await validateAndGetSession();
      if (!session) {
        showUploadError('Session expired. Please refresh the page.', 'SESSION_EXPIRED');
        return;
      }
      
      const url = await uploadToCloudinary(file, user?.id);
      setData((prev) => ({ ...prev, customThumbnail: url }));
      setImageSrc(url);
    } catch (err: any) {
      console.error('Thumbnail upload failed', err);
      showUploadError(err.message || 'Thumbnail upload failed. Please try again.', 'THUMBNAIL_UPLOAD_FAILED');
    } finally {
      setIsUploading(false);
    }
  };

  const checkYouTubeToken = async (session: Session) => {
    if (!user || !session?.access_token) return false;
    
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/youtube_tokens?select=access_token&user_id=eq.${user.id}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
          Accept: 'application/json',
        },
      });

      if (!response.ok) return false;
      
      const tokens = await response.json();
      return Array.isArray(tokens) && tokens.length > 0 && !!tokens[0].access_token;
    } catch (err) {
      console.error('Token check error:', err);
      return false;
    }
  };

  const handleSaveDraft = async () => {
    if (!user) {
      showUploadError('Please sign in to save drafts.', 'AUTH_REQUIRED');
      return;
    }
    
    const validation = validateFiles();
    if (!validation.isValid) {
      showUploadError(validation.message || 'Invalid upload input.', 'INVALID_INPUT');
      return;
    }

    const session = await validateAndGetSession();
    if (!session) {
      showUploadError('Session expired. Please refresh the page and try again.', 'SESSION_EXPIRED');
      return;
    }

    try {
      setIsUploading(true);

      let audioUrl = null;
      let videoUrl = null;
      let thumbUrl = null;

      const uploadPromises = [];
      
      if (data.audio instanceof File) {
        uploadPromises.push(
          uploadToCloudinary(data.audio, user.id).then(url => { audioUrl = url; })
        );
      }
      
      if (data.video instanceof File) {
        uploadPromises.push(
          uploadToCloudinary(data.video, user.id).then(url => { videoUrl = url; })
        );
      }
      
      if (data.customThumbnail instanceof File) {
        uploadPromises.push(
          uploadToCloudinary(data.customThumbnail, user.id).then(url => { thumbUrl = url; })
        );
      } else if (typeof data.customThumbnail === 'string') {
        thumbUrl = data.customThumbnail;
      }

      await Promise.all(uploadPromises);

      const mediaType = videoUrl ? 'mp4' : 'mp3';
      const mediaUrl = videoUrl || audioUrl;

      const { error } = await upsertSupabaseDraft(
        {
          id: data.id || crypto.randomUUID(),
          title: data.title,
          description: data.description,
          tags: data.tags || [],
          mediaUrl,
          mediaType,
          thumbnailUrl: thumbUrl,
          visibility: data.visibility,
          status: 'draft',
          scheduledAt:
            data.publishTiming === 'schedule' && data.scheduledAt
              ? new Date(data.scheduledAt).toISOString()
              : null,
        },
        user.id
      );

      if (error) throw error;

      if (onSaveDraft) onSaveDraft();
    } catch (err: any) {
      console.error('Save draft error', err);
      showUploadError(err.message || 'Failed to save draft', 'DRAFT_SAVE_FAILED');
    } finally {
      setIsUploading(false);
    }
  };

  // 🆕 Layer 1: Pre-Upload Token Validation with Auto-Refresh
  const validateTokenFreshness = async (session: Session): Promise<Session> => {
    try {
      console.log('🔍 Validating token freshness...');
      
      // Check if token expires soon (within 5 minutes)
      if (session.expires_at) {
        const expiresAt = new Date(session.expires_at * 1000);
        const now = new Date();
        const timeUntilExpiry = expiresAt.getTime() - now.getTime();
        const fiveMinutes = 5 * 60 * 1000;
        
        if (timeUntilExpiry < fiveMinutes) {
          console.log('⚠️ Token expires in less than 5 minutes, preemptively refreshing...');
          
          const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError || !refreshedSession) {
            console.error('❌ Token refresh failed:', refreshError);
            throw new Error('TOKEN_REFRESH_FAILED');
          }
          
          console.log('✅ Token refreshed successfully');
          setCurrentSession(refreshedSession);
          setSessionError(null);
          return refreshedSession;
        }
      }
      
      // Verify YouTube token exists and is valid
      console.log('🔍 Verifying YouTube token validity...');
      const hasValidToken = await checkYouTubeToken(session);
      if (!hasValidToken) {
        throw new Error('YOUTUBE_TOKEN_INVALID');
      }
      
      console.log('✅ Token validation passed');
      return session;
      
    } catch (err: any) {
      console.error('❌ Token validation failed:', err);
      
      if (err.message === 'TOKEN_REFRESH_FAILED') {
        showUploadError('Session expired and could not be refreshed. Please sign in again.', 'TOKEN_REFRESH_FAILED');
        throw err;
      }
      
      if (err.message === 'YOUTUBE_TOKEN_INVALID') {
        showUploadError('YouTube authorization expired. Please reconnect your YouTube account.', 'TOKEN_EXPIRED');
        throw err;
      }
      
      throw err;
    }
  };

  const handleUpload = async () => {
    if (!user) {
      showUploadError('Please sign in to upload.', 'AUTH_REQUIRED');
      return;
    }

    if (!isSessionReady) {
      showUploadError('Session not ready. Please wait and try again.', 'SESSION_NOT_READY');
      return;
    }

    const validation = validateFiles();
    if (!validation.isValid) {
      showUploadError(validation.message || 'Invalid upload input.', 'INVALID_INPUT');
      return;
    }

    console.log('🔍 Step 1: Pre-upload session validation...');
    const session = await validateAndGetSession();
    if (!session) {
      showUploadError('Session expired or invalid. Please refresh the page and sign in again.', 'SESSION_EXPIRED');
      return;
    }

    console.log('🔍 Step 2: Validating token freshness and auto-refreshing if needed...');
    let validSession: Session;
    try {
      validSession = await validateTokenFreshness(session);
    } catch (err: any) {
      console.error('Token validation failed, aborting upload');
      return;
    }

    // Create upload ID
    const uploadId = `upload-${Date.now()}`;
    setCurrentUploadId(uploadId);
    
    // Check if another upload is currently processing
    const hasActiveUpload = uploads.some(u => u.status === 'uploading');
    
    if (hasActiveUpload) {
      // Queue this upload
      console.log('⏸️ Another upload in progress, queuing this one...');
      
      addUpload({
        id: uploadId,
        title: data.title,
        platforms: ['YouTube'],
        status: 'queued',
        progress: 0,
        timeLeft: 'Waiting in queue...',
        publishTiming: data.publishTiming,
        scheduledAt: data.scheduledAt,
      });
      
      onSubmit('', '', ''); // Close modal
      
      // Store upload data in global Map (Files can't be JSON serialized)
      queuedUploadsData.set(uploadId, {
        data,
        userId: user.id,
        session: validSession
      });

        await saveUploadRetry({
          uploadId,
          userId: user.id,
          data,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        });
      
      return;
    }
    
    // No active upload, start immediately
    addUpload({
      id: uploadId,
      title: data.title,
      platforms: ['YouTube'],
      status: 'uploading',
      progress: 0,
      timeLeft: 'Calculating...',
      startedAt: Date.now(),
      publishTiming: data.publishTiming,
      scheduledAt: data.scheduledAt,
    });

    onSubmit('', '', ''); // Close modal immediately

    await saveUploadRetry({
      uploadId,
      userId: user.id,
      data,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });

    // Use the new processUpload function
    const { processUpload } = await import('@/lib/processUpload');
    
    processUpload(
      uploadId,
      {
        video: data.video,
        audio: data.audio,
        customThumbnail: data.customThumbnail,
        title: data.title,
        description: data.description,
        tags: data.tags || [],
        visibility: data.visibility,
        publishTiming: data.publishTiming,
        scheduledAt: data.scheduledAt
      },
      user.id,
      validSession,
      {
        onProgress: (progress, message) => {
          console.log(`📊 [${uploadId}] Progress: ${progress}% - ${message}`);
          updateUpload(uploadId, { 
            progress, 
            timeLeft: message 
          });
        },
        onComplete: (videoId, youtubeUrl, thumbnailUrl) => {
          console.log(`✅ [${uploadId}] Upload completed successfully`);
          updateUpload(uploadId, { 
            status: 'completed', 
            progress: 100,
          });
          void deleteUploadRetry(uploadId);
          
          // Trigger dashboard refresh
          window.dispatchEvent(new Event('refresh-uploads'));
          
          // Call original onSubmit with video details
          onSubmit(videoId, youtubeUrl, thumbnailUrl);
        },
        onCheckpoint: (updates) => updateUploadRetry(uploadId, { data: updates }),
        onError: (error, code) => {
          console.error(`❌ [${uploadId}] Upload failed:`, error);
          updateUpload(uploadId, { 
            status: 'failed',
            timeLeft: error,
          });
          
          setUploadError({
            error: true,
            step: 'upload',
            message: error,
            code: code || 'UNKNOWN_ERROR',
            requestId: uploadId
          });
        }
      }
    );
    
    // Reset component state
    setIsPublishing(false);
    setUploadStage('');
  };
  const uploadToYouTube = async (uploadUrl: string, payload: any, session: Session): Promise<UploadResult> => {
    const requestId = `upload-${Date.now()}`;
    setUploadRequestId(requestId);
    
    console.log(`🚀 [${requestId}] Starting YouTube upload`);

    if (!session.access_token) {
      throw new Error('Invalid session: missing access token');
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10 * 60 * 1000);

      setUploadStage(`Uploading to YouTube...`);

      const response = await fetch(`${uploadUrl}/youtube-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [${requestId}] HTTP Error ${response.status}:`, errorText);
        
        let errorMessage = `Upload failed: ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          errorMessage = errorText && errorText.length < 200 ? errorText : `HTTP ${response.status} Error`;
        }
        
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      let result: UploadResult;

      if (contentType?.includes('text/event-stream') || contentType?.includes('text/plain')) {
        const responseText = await response.text();
        const lines = responseText.split('\n');
        let finalData = null;
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.substring(6);
              const parsed = JSON.parse(jsonStr);
              
              if (parsed.videoId || parsed.success === true || parsed.youtubeUrl) {
                finalData = parsed;
              }
            } catch (parseErr) {
              // Continue to next line
            }
          }
        }
        
        if (finalData?.error === true) {
          throw new Error(finalData.message || 'YouTube upload failed on backend');
        }
        
        if (!finalData) {
          throw new Error(`No valid data found in server response`);
        }
        
        result = finalData as UploadResult;
      } else {
        const jsonResult = await response.json();
        
        if (jsonResult?.error === true) {
          throw new Error(jsonResult.message || 'YouTube upload failed on backend');
        }
        
        result = jsonResult as UploadResult;
      }

      if (!result.videoId && !result.youtubeUrl) {
        throw new Error(`Invalid response: missing videoId or youtubeUrl`);
      }
      
      return result;
    } catch (err: any) {
      console.error(`❌ [${requestId}] YouTube upload error:`, err);

      if (err.name === 'AbortError') {
        throw new Error('Upload timed out after 10 minutes. Please try again.');
      }

      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        throw new Error(`Network error. Make sure your backend server is running on ${uploadUrl}`);
      }

      throw new Error(err.message || 'YouTube upload failed. Check console for details.');
    }
  };

  const getStepIcon = (step: string) => {
    switch (step) {
      case 'authentication':
        return <Youtube className="w-4 h-4 text-red-500" />;
      case 'downloading':
      case 'uploading':
        return <CloudUpload className="w-4 h-4 text-blue-500" />;
      case 'thumbnail':
        return <ImageIcon className="w-4 h-4 text-green-500" />;
      case 'saving':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      default:
        return <Upload className="w-4 h-4 text-primary" />;
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-xl">Visibility</h2>

      {(conversionError || uploadError) && (
        <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-lg flex gap-3 items-start">
          <AlertCircle className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium">
              {conversionError ? 'Conversion Failed' : 'Upload Failed'}
            </p>
            <p className="text-sm">
              {conversionError || uploadError?.message}
            </p>
            {uploadError?.requestId && (
              <p className="text-xs mt-1 opacity-80">
                Request ID: {uploadError.requestId}
              </p>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={() => {
                resetConversion();
                setUploadError(null);
                setUploadProgress(null);
                setSessionError(null);
              }}
            >
              Try Again
            </Button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-surface border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-foreground" />
              <Label className="text-base  text-foreground">Publish Timing</Label>
            </div>
            <p className="text-sm text-foreground/60 mb-4">
              Select a date and time to make your video public
            </p>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  data.publishTiming === 'now' 
                    ? 'border-foreground bg-foreground' 
                    : 'border-border group-hover:border-foreground/50'
                }`}>
                  {data.publishTiming === 'now' && (
                    <div className="w-2 h-2 rounded-full bg-background" />
                  )}
                </div>
                <input
                  type="radio"
                  className="sr-only"
                  checked={data.publishTiming === 'now'}
                  onChange={() => setData((p) => ({ ...p, publishTiming: 'now', scheduledAt: '' }))}
                />
                <span className="text-foreground">Publish Now</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  data.publishTiming === 'schedule' 
                    ? 'border-foreground bg-foreground' 
                    : 'border-border group-hover:border-foreground/50'
                }`}>
                  {data.publishTiming === 'schedule' && (
                    <div className="w-2 h-2 rounded-full bg-background" />
                  )}
                </div>
                <input
                  type="radio"
                  className="sr-only"
                  checked={data.publishTiming === 'schedule'}
                  onChange={() => setData((p) => ({ ...p, publishTiming: 'schedule' }))}
                />
                <span className="text-foreground">Schedule For Later</span>
              </label>
            </div>

            {data.publishTiming === 'schedule' && (
              <div className="mt-4 pt-4 border-t border-border">
                <UploadDateTimePicker
                  scheduledDates={highlightedDates.scheduled}
                  publishedDates={highlightedDates.published || []}
                  selectedDateTime={data.scheduledAt}
                  onDateTimeChange={(datetime) => setData((p) => ({ 
                    ...p, 
                    scheduledAt: datetime 
                  }))}
                  itemsByDate={itemsByDate}
                />
              </div>
            )}
          </div>

          {data.video && (
            <div className="bg-surface border border-border rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <ImageIcon className="w-5 h-5 text-foreground" />
                <Label className="text-base  text-foreground">Custom Thumbnail</Label>
              </div>
              
              <label className="block border-2 border-dashed border-border rounded-lg p-6 cursor-pointer hover:border-foreground/50 hover:bg-surface-elevated/30 transition-all">
                <Input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleThumbnailUpload(file);
                  }}
                />
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className="w-12 h-12 rounded-lg bg-surface-elevated flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-foreground/60" />
                  </div>
                  <span className="text-sm text-foreground">
                    {isUploading ? 'Uploading...' : 'Click to upload thumbnail'}
                  </span>
                </div>
              </label>
              
              {imageSrc && (
                <div className="mt-3 flex items-center gap-2 text-sm text-green-500">
                  <CheckCircle2 className="w-4 h-4" />
                  Thumbnail ready for upload
                </div>
              )}
            </div>
          )}

          <div className="bg-surface border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-5 h-5 text-foreground" />
              <Label className="text-basetext-foreground">Visibility</Label>
            </div>
            <p className="text-sm text-foreground/60 mb-4">
              Make your video public, unlisted, or private
            </p>
            
            <Select value={data.visibility} onValueChange={(value) => setData((p) => ({ ...p, visibility: value }))}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="unlisted">Unlisted</SelectItem>
                <SelectItem value="private">Private</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Progress removed - now shown in BackgroundUploader */}
        </div>

        <div className="sticky top-0">
          <MediaPreviewCard
            audioSrc={audioSrc}
            videoSrc={videoSrc}
            imageSrc={imageSrc}
            title={data.title}
            description={data.description}
          />
        </div>
      </div>

      <div className="flex justify-between mt-6">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={isPublishing || isConverting}
        >
          Previous
        </Button>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={handleSaveDraft}
            disabled={isUploading || isPublishing || isConverting || !!sessionError || !isSessionReady}
          >
            {isUploading ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button
            onClick={handleUpload}
            disabled={
              isPublishing || 
              isConverting || 
              !validateFiles().isValid || 
              !!sessionError || 
              !isSessionReady || 
              !currentSession
            }
            className="min-w-[140px]"
          >
            {isConverting
              ? `Converting... ${conversionProgress}%`
              : isPublishing
              ? uploadProgress?.message || uploadStage || 'Publishing...'
              : 'Upload to YouTube'}
          </Button>
        </div>
      </div>
    </div>
  );
}